import { Helmet } from "react-helmet-async";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  twitterImage?: string;
  canonical?: string;
  noIndex?: boolean;
  children?: React.ReactNode;
}

export function SEO({
  title = "Saasai - AI Systems Studio",
  description = "Transform your business with Saasai's intelligent automation and AI agents. Build, deploy, and manage custom AI solutions that scale with your needs.",
  keywords = "AI automation, artificial intelligence, business automation, AI agents",
  ogTitle,
  ogDescription,
  ogImage = "https://saasai.com/og-image.png",
  ogUrl,
  twitterTitle,
  twitterDescription,
  twitterImage = "https://saasai.com/twitter-image.png",
  canonical,
  noIndex = false,
  children,
}: SEOProps) {
  const fullTitle = title.includes("Saasai") ? title : `${title} | Saasai`;
  const finalOgTitle = ogTitle || fullTitle;
  const finalOgDescription = ogDescription || description;
  const finalTwitterTitle = twitterTitle || finalOgTitle;
  const finalTwitterDescription = twitterDescription || finalOgDescription;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}

      {/* Canonical URL */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Robots */}
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:title" content={finalOgTitle} />
      <meta property="og:description" content={finalOgDescription} />
      <meta property="og:image" content={ogImage} />
      {ogUrl && <meta property="og:url" content={ogUrl} />}

      {/* Twitter */}
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={twitterImage} />

      {children}
    </Helmet>
  );
}

// Predefined SEO configurations for common pages
export const HomeSEO = () => (
  <SEO
    title="Saasai - AI Systems Studio | Intelligent Automation Solutions"
    description="Transform your business with Saasai's intelligent automation and AI agents. Build, deploy, and manage custom AI solutions that scale with your needs."
    keywords="AI automation, artificial intelligence, business automation, AI agents, machine learning, workflow automation"
  />
);

export const BuildConfiguratorSEO = () => (
  <SEO
    title="Build Your AI Solution"
    description="Configure and customize your AI solution with our intuitive builder. Get instant quotes and deployment timelines."
    keywords="AI builder, custom AI solution, AI configuration, automation builder"
  />
);

export const ClientPortalSEO = () => (
  <SEO
    title="Client Portal"
    description="Access your projects, monitor deployments, and manage your AI solutions in one place."
    noIndex={true}
  />
);

export const BillingSEO = () => (
  <SEO
    title="Billing & Payments"
    description="Manage your subscriptions, view invoices, and update payment methods."
    noIndex={true}
  />
);

export const AdminLoginSEO = () => (
  <SEO
    title="Admin Login"
    description="Secure administrator login for Saasai dashboard."
    noIndex={true}
  />
);

export const AdminDashboardSEO = () => (
  <SEO
    title="Admin Dashboard"
    description="Manage orders, leads, deployments, and analytics from your admin dashboard."
    noIndex={true}
  />
);

export default SEO;
